<script setup lang="ts">
import { mdiAutoFix } from '@mdi/js'

// Tag master — 将来的にはAPIから取得する
const TAG_GROUPS = [
  {
    label: 'ジャンル',
    tags: ['アクション', 'シューティング', 'パズル', 'RPG', 'アドベンチャー', 'シミュレーション', 'リズム', 'レース'],
  },
  {
    label: 'テイスト',
    tags: ['ハイスピード', 'リラックス', 'ホラー', 'コメディ', 'かわいい', 'ハードコア', 'ファンタジー', 'SF', '現代'],
  },
  {
    label: 'つくりこみ',
    tags: ['シンプル', '大作', '実験作', 'ツール'],
  },
] as const

interface ThumbnailOption {
  dataUrl: string
  // #707: OGP still (PNG data URL) paired with this GIF thumbnail. undefined for
  // old clients / when the paired still didn't arrive → publish falls back to
  // cover-only.
  stillDataUrl?: string
}

interface Props {
  thumbnails?: ThumbnailOption[]
  initialTitle?: string
  projectId?: string
  isGithub?: boolean
  /**
   * #666: クリスタル化対象として preview セッションを作ったときと同じ entrypoint .py。
   * 複数 .py プロジェクトで非 first ファイルを公開する場合、LLM 審査 (Layer 2) が
   * 正しいファイルを見るために backend に渡す。指定しなければ最初の .py に
   * フォールバックする (backend と同じ解決ロジック)。
   */
  entrypoint?: string
  /**
   * #787: 送信中（クリスタル化の API 待ち）。親 (`cradle.vue`) が持つ。
   * true のあいだダイアログは閉じず、工程表示に切り替わる。
   */
  submitting?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  thumbnails: () => [],
  initialTitle: '',
  projectId: undefined,
  isGithub: false,
  entrypoint: undefined,
})

const emit = defineEmits(['submit'])

const { generateSlug: generateSlugApi } = useApi()

const dialogStore = useDialogStore()
const dialog = dialogStore.crystallize
const dialogParams = computed(() => dialog.params)

const title = ref('')
const description = ref('')
const slug = ref('')
const selectedTags = ref<string[]>([])
const termsPolicy = ref(false)
const termsContent = ref(false)
const termsOriginal = ref(false)
const termsRemix = ref(false)
const termsViolation = ref(false)
const gamepadCompatible = ref(false)

// #842: 自分で作った素材を、ほかの人が使ってよいことにするかを選ぶ。
//
// 既定を「あとで決める」と書いていたのは誤り。**クリスタル化は公開そのもの**なので、
// 見る側はその場で「使ってよいのか」を知りたい。選ばないことの本当の意味は
// 「Code & Magic の外へは何も許諾しない」であって、宙ぶらりんの未定ではない。
//
// C&M の**中**でリミックスできるかどうかは `Work.remixable` が別に決める。
// ここで決めるのは**外に出してよいか**だけ。
//
// 値は backend の `SELECTABLE_LICENSES` と揃えること（知らない値は既定に倒される）。
const LICENSE_CHOICES = [
  { value: 'user-owned', title: 'Code & Magic 内は使ってよい' },
  { value: 'CC0-1.0', title: 'CC0 — 自由に使ってよい' },
  { value: 'CC-BY-4.0', title: 'CC BY — 名前を書けば使ってよい' },
  { value: 'MIT', title: 'MIT — 著作権表示をつければ使ってよい（コード向け）' },
]
const chosenLicense = ref('user-owned')
const selectedThumbnailIndex = ref<number>(0)
const checking = ref(false)

// screening status: 'idle' | 'passed' | 'failed'
const screeningStatus = ref<'idle' | 'passed' | 'failed'>('idle')
const screeningMessage = ref('')
// 失敗が code 審査由来なら「公開コードのルール」記事へのリンクを出す
const screeningFailureType = ref<'content' | 'code' | null>(null)

// フィールド編集時にチェック結果をリセット
watch([title, description, selectedTags, termsPolicy, termsContent, termsOriginal, termsRemix, termsViolation], () => {
  if (screeningStatus.value !== 'idle') {
    screeningStatus.value = 'idle'
    screeningMessage.value = ''
    screeningFailureType.value = null
    slug.value = ''
  }
})

// ダイアログ表示時にプロジェクト名をプリフィル、閉じたらクリア
watch(() => dialog.visible, (visible) => {
  if (visible) {
    if (props.initialTitle) {
      title.value = props.initialTitle
    }
  }
  else {
    title.value = ''
    description.value = ''
    slug.value = ''
    selectedTags.value = []
    termsPolicy.value = false
    termsContent.value = false
    termsOriginal.value = false
    termsRemix.value = false
    termsViolation.value = false
    gamepadCompatible.value = false
    chosenLicense.value = 'user-owned'
    selectedThumbnailIndex.value = 0
    screeningStatus.value = 'idle'
    screeningMessage.value = ''
    screeningFailureType.value = null
  }
})

const onCheckContent = async () => {
  if (!title.value.trim() || !description.value.trim()) return
  checking.value = true
  screeningStatus.value = 'idle'
  screeningMessage.value = ''
  screeningFailureType.value = null
  slug.value = ''
  try {
    const raw = await generateSlugApi(
      title.value.trim(),
      description.value.trim() || undefined,
      props.projectId,
      props.entrypoint,
    )
    if (raw.screening && !raw.screening.approved) {
      screeningStatus.value = 'failed'
      screeningFailureType.value = 'content'
      screeningMessage.value = raw.screening.reason || 'タイトルまたは説明文が公開基準を満たしていません。内容を修正してください。'
      slug.value = ''
      return
    }
    if (raw.codeScreening && !raw.codeScreening.approved) {
      screeningStatus.value = 'failed'
      screeningFailureType.value = 'code'
      screeningMessage.value = raw.codeScreening.reason || 'コードの内容が説明と一致しないか、公開基準を満たしていません。'
      slug.value = ''
      return
    }
    screeningStatus.value = 'passed'
    screeningFailureType.value = null
    screeningMessage.value = 'チェックに合格しました。クリスタル化できます。'
    slug.value = raw.slug
  }
  catch (e) {
    console.error('content check failed', e)
  }
  finally {
    checking.value = false
  }
}

const toggleTag = (tag: string) => {
  const idx = selectedTags.value.indexOf(tag)
  if (idx >= 0) {
    selectedTags.value.splice(idx, 1)
  }
  else {
    selectedTags.value.push(tag)
  }
}

const DESCRIPTION_MIN_LENGTH = 20
const DESCRIPTION_MAX_LENGTH = 200
const TITLE_MAX_LENGTH = 50

const descriptionRules = [
  (v: string) => !!v || '説明は必須です',
  (v: string) => (v && v.trim().length >= DESCRIPTION_MIN_LENGTH) || `説明は${DESCRIPTION_MIN_LENGTH}文字以上で書いてください`,
  (v: string) => v.length <= DESCRIPTION_MAX_LENGTH || `説明は${DESCRIPTION_MAX_LENGTH}文字以内で書いてください`,
]

const titleRules = [
  (v: string) => !!v || 'タイトルは必須です',
  (v: string) => v.length <= TITLE_MAX_LENGTH || `タイトルは${TITLE_MAX_LENGTH}文字以内で書いてください`,
]

const allTermsAccepted = computed(() => {
  const remixOk = props.isGithub || termsRemix.value
  return termsPolicy.value && termsContent.value && termsOriginal.value && remixOk && termsViolation.value
})

const isValid = computed(() => {
  return title.value.trim().length > 0
    && description.value.trim().length >= DESCRIPTION_MIN_LENGTH
    && selectedTags.value.length > 0
    && slug.value.length > 0
    && allTermsAccepted.value
})

const selectedThumbnail = computed(() => {
  if (props.thumbnails.length === 0) return undefined
  return props.thumbnails[selectedThumbnailIndex.value]?.dataUrl
})

// #707: OGP still paired with the selected thumbnail (same capture tick).
const selectedStill = computed(() => {
  if (props.thumbnails.length === 0) return undefined
  return props.thumbnails[selectedThumbnailIndex.value]?.stillDataUrl
})

// #787 (client 側 fallback) + #791 MR6 (server-driven 優先)。
//
// MR5 まで: サーバは工程を返さないので client 側の推定タイマーで進めていた。
// MR6 以降: sfn mode で server が abstract TaskState + ceremonyLabel を返すので、
// **server label があれば優先**、無ければ client の fallback タイマーを使う (inline
// mode の rollback path でも従来の見た目が保たれる)。
const { label: fallbackLabel, start: startCeremony, stop: stopCeremony } = useCeremonyStages([
  { label: '魔導書を書き写しています…', durationMs: 3000 },
  { label: '結晶に鍛えあげています…', durationMs: 4000 },
  { label: '表紙を焼きつけています…', durationMs: 4000 },
  // 最後の工程は自動では進まない。完了はサーバの応答が決める。
  { label: '魔法研究会に届けています…', durationMs: 0 },
])
// #791 MR6: useEventSystem の taskLabel (server ceremony_label) を購読する。
// task step が active でない (inline mode + 直接 create_work 経由) 場合は空文字なので
// fallback ラベルにフォールバックする。
const eventSystemStore = useEventSystemStore()
const ceremonyLabel = computed(() => eventSystemStore.taskLabel || fallbackLabel.value)

watch(() => props.submitting, (busy) => {
  if (busy) startCeremony()
  else stopCeremony()
})

const onSubmit = () => {
  if (!isValid.value) return
  emit('submit', {
    title: title.value.trim(),
    description: description.value.trim(),
    slug: slug.value.trim(),
    tags: [...selectedTags.value],
    remixable: !props.isGithub,
    gamepadCompatible: gamepadCompatible.value,
    license: chosenLicense.value,
    thumbnailData: selectedThumbnail.value,
    ogpImageData: selectedStill.value,
  })
}

const onCancel = () => {
  dialogStore.closeDialog('crystallize', 'cancel')
}
</script>

<template>
  <DialogLargeDialog
    v-if="dialogParams && dialog.visible"
    :title="dialogParams.title"
    :model-value="dialog.visible"
    :cancel-button="dialogParams.actions.cancel ? { title: dialogParams.actions.cancel.label, sound: dialogParams.actions.cancel.sound, disabled: dialogParams.actions.cancel.disabled } : undefined"
    :submit-button="{ title: submitting ? 'クリスタル化中…' : 'クリスタル化する', disabled: !isValid }"
    keep-open-on-submit
    :busy="submitting"
    @cancel="onCancel"
    @submit="onSubmit"
  >
    <template #default>
      <!-- #787: 送信中はフォームの上に工程を重ねる。押した対象がその場で反応し、
           視線を移動させずに「いま何が起きているか」が読める。 -->
      <div
        v-if="submitting"
        class="ceremony-overlay"
      >
        <v-progress-circular
          color="brown"
          size="64"
          indeterminate
        >
          <v-icon
            size="x-large"
            color="brown"
          >
            {{ mdiAutoFix }}
          </v-icon>
        </v-progress-circular>
        <p class="ceremony-label">
          {{ ceremonyLabel }}
        </p>
        <p class="ceremony-note">
          このまま少しお待ちください。
        </p>
      </div>

      <v-form class="crystallize-form">
        <!-- Thumbnail selector -->
        <div
          v-if="thumbnails.length > 0"
          class="thumbnail-section"
        >
          <div class="thumbnail-label">
            サムネイルを選択
          </div>
          <div class="thumbnail-grid">
            <div
              v-for="(thumb, index) in thumbnails"
              :key="index"
              class="thumbnail-item"
              :class="{ selected: selectedThumbnailIndex === index }"
              @click="selectedThumbnailIndex = index"
            >
              <img
                :src="thumb.dataUrl"
                :alt="`サムネイル ${index + 1}`"
                class="thumbnail-img"
              >
            </div>
          </div>
        </div>

        <v-text-field
          v-model="title"
          label="タイトル"
          :rules="titleRules"
          :counter="TITLE_MAX_LENGTH"
          :maxlength="TITLE_MAX_LENGTH"
          variant="outlined"
          density="compact"
          class="mb-2"
        />
        <v-textarea
          v-model="description"
          label="説明"
          :rules="descriptionRules"
          :counter="DESCRIPTION_MAX_LENGTH"
          :maxlength="DESCRIPTION_MAX_LENGTH"
          variant="outlined"
          density="compact"
          rows="2"
          class="mb-2"
        />
        <div class="tag-section mb-2">
          <div class="tag-label">
            タグ <span
              v-if="selectedTags.length === 0"
              class="tag-required"
            >※ 1つ以上選択してください</span>
          </div>
          <div
            v-for="group in TAG_GROUPS"
            :key="group.label"
            class="tag-group"
          >
            <span class="tag-group-label">{{ group.label }}:</span>
            <button
              v-for="tag in group.tags"
              :key="tag"
              type="button"
              :class="['tag-chip', { selected: selectedTags.includes(tag) }]"
              @click="toggleTag(tag)"
            >
              {{ tag }}
            </button>
          </div>
        </div>
        <!-- 同意事項 -->
        <div class="terms-section">
          <div class="terms-text">
            クリスタル化するために、それぞれ確認してチェックしてください:
          </div>
          <v-checkbox
            v-model="termsPolicy"
            density="compact"
            hide-details
            class="terms-checkbox"
          >
            <template #label>
              この作品は<NuxtLink
                to="/docs/publishing_policy"
                target="_blank"
                class="text-blue"
              >公開作品ポリシー</NuxtLink>のルールに従って作りました
            </template>
          </v-checkbox>
          <v-checkbox
            v-model="termsContent"
            label="みんなが気持ちよく遊べるよう、ふさわしくない内容は公開しません"
            density="compact"
            hide-details
            class="terms-checkbox"
          />
          <v-checkbox
            v-model="termsOriginal"
            label="公開するコードは自分で作ったもの、または公開してよいと決められたものです"
            density="compact"
            hide-details
            class="terms-checkbox"
          />
          <v-checkbox
            v-if="!isGithub"
            v-model="termsRemix"
            label="ほかのユーザーがこの作品をもとに新しい作品を作ること（リミックス）を許可します"
            density="compact"
            hide-details
            class="terms-checkbox"
          />
          <v-checkbox
            v-else
            model-value="true"
            label="黒い魔導書のクリスタルはリミックスできません"
            density="compact"
            hide-details
            disabled
            class="terms-checkbox"
          />
          <v-checkbox
            v-model="termsViolation"
            label="ルール違反があった場合、作品が非公開になったり、アカウントが制限されることに同意します"
            density="compact"
            hide-details
            class="terms-checkbox"
          />
          <div class="terms-note">
            公開した作品は、Code &amp; Magic の SNS アカウントで紹介されることがあります（<NuxtLink
              to="/articles/works-on-sns"
              target="_blank"
              class="text-blue"
            >くわしく</NuxtLink>）。
          </div>
        </div>
        <!-- 作品オプション（任意） -->
        <div class="options-section">
          <div class="options-text">
            この作品の対応について教えてください（任意）:
          </div>
          <v-checkbox
            v-model="gamepadCompatible"
            label="仮想ゲームパッド対応 — スマホ／タブレットで仮想ゲームパッドが表示されます。十字キー・A/B キーをハンドルしているコードのみチェックしてください"
            density="compact"
            hide-details
            class="options-checkbox"
          />
        </div>

        <!-- #842: 自分で作った素材の使いかた。選ばなければ「未決定」のまま。 -->
        <div class="options-section">
          <div class="options-text">
            あなたが作った絵・音・コードを、ほかの人が使ってもよいことにしますか？（任意）:
          </div>
          <v-select
            v-model="chosenLicense"
            :items="LICENSE_CHOICES"
            density="compact"
            variant="outlined"
            hide-details
            class="license-select"
          />
          <div class="options-note">
            引き継いだ素材の作者は変わりません。あとから作品を作り直して選び直せます。
            <NuxtLink
              to="/licenses/user-owned"
              target="_blank"
              class="options-link"
            >くわしく</NuxtLink>
          </div>
        </div>
        <!-- 入力内容チェック + スラッグ -->
        <div class="check-section">
          <div class="check-row">
            <v-btn
              variant="tonal"
              :loading="checking"
              :disabled="!title.trim() || description.trim().length < DESCRIPTION_MIN_LENGTH || selectedTags.length === 0 || !allTermsAccepted || checking"
              class="check-btn"
              @click="onCheckContent"
            >
              入力内容をチェック
            </v-btn>
            <v-text-field
              v-model="slug"
              label="スラッグ（URL用ID）— タイトルと説明から自動生成"
              variant="outlined"
              density="compact"
              disabled
              hide-details
              class="slug-field"
            />
          </div>
          <!-- チェック結果: 常設エリア（レイアウトシフトなし） -->
          <div
            class="screening-status"
            :class="{
              'screening-status--passed': screeningStatus === 'passed',
              'screening-status--failed': screeningStatus === 'failed',
              'screening-status--idle': screeningStatus === 'idle',
            }"
          >
            <template v-if="screeningStatus !== 'idle'">
              {{ screeningMessage }}
              <NuxtLink
                v-if="screeningFailureType === 'code'"
                to="/articles/publish-code-rules"
                target="_blank"
                rel="noopener"
                class="screening-rules-link"
                data-testid="publish-code-rules-link"
              >
                公開コードのルール →
              </NuxtLink>
            </template>
            <template v-else>
              &nbsp;
            </template>
          </div>
        </div>
      </v-form>
    </template>
  </DialogLargeDialog>
</template>

<style scoped>
/* #787: 送信中の工程表示。フォームの上に重ねるので、押した対象（ダイアログ）から
   視線を動かさずに状況が読める。親の .fantasy-dialog-text が position: relative。 */
.ceremony-overlay {
  position: absolute;
  inset: 0;
  z-index: 2;

  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: max(8.89px, calc(1.25 * var(--u)));

  /* 下のフォームをうっすら透かして「戻ってこられる」感じを残す。 */
  background-color: rgba(245, 211, 140, 0.92);
}

.ceremony-label {
  margin: 0;
  font-size: max(15px, calc(2.1 * var(--u)));
  font-weight: bold;
  color: #4a3520;
  text-align: center;
}

.ceremony-note {
  margin: 0;
  font-size: max(11px, calc(1.5 * var(--u)));
  color: #6b563d;
  text-align: center;
}

.crystallize-form {
  padding: max(8px, calc(1 * var(--u)));
}

.crystallize-form :deep(.v-field) {
  font-size: max(11px, calc(1.5 * var(--u)));
}

.crystallize-form :deep(.v-label) {
  font-size: max(11px, calc(1.5 * var(--u)));
}

.crystallize-form :deep(.v-messages) {
  font-size: max(9px, calc(1.2 * var(--u)));
}

.thumbnail-section {
  margin-bottom: max(8px, calc(1 * var(--u)));
}

.thumbnail-label {
  font-size: max(11px, calc(1.5 * var(--u)));
  font-weight: bold;
  margin-bottom: max(4px, calc(0.5 * var(--u)));
}

.thumbnail-grid {
  display: flex;
  gap: max(4px, calc(0.5 * var(--u)));
  overflow-x: auto;
  padding: max(4px, calc(0.5 * var(--u))) 0;
}

.thumbnail-item {
  flex: 0 0 auto;
  cursor: pointer;
  border: 4px solid transparent;
  border-radius: 4px;
  padding: 3px;
  text-align: center;
  transition: border-color 0.2s, box-shadow 0.2s;
}

.thumbnail-item.selected {
  border-color: #42a5f5;
  box-shadow: 0 0 8px rgba(66, 165, 245, 0.6);
  background-color: rgba(66, 165, 245, 0.12);
}

.thumbnail-item:hover:not(.selected) {
  border-color: #90caf9;
}

.thumbnail-img {
  width: max(80px, calc(10 * var(--u)));
  height: auto;
  image-rendering: pixelated;
  border-radius: 2px;
  display: block;
}

.tag-label {
  font-size: max(11px, calc(1.5 * var(--u)));
  color: rgba(0, 0, 0, 0.6);
  margin-bottom: max(4px, calc(0.5 * var(--u)));
}

.tag-group {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: max(4px, calc(0.5 * var(--u)));
  margin-bottom: max(3px, calc(0.3 * var(--u)));
}

.tag-group-label {
  font-size: max(9px, calc(1 * var(--u)));
  color: rgba(0, 0, 0, 0.45);
  white-space: nowrap;
}

.tag-chip {
  padding: max(1px, calc(0.2 * var(--u))) max(5px, calc(0.7 * var(--u)));
  font-size: max(9px, calc(1.1 * var(--u)));
  border: 1px solid #455A64;
  border-radius: 2px;
  background: transparent;
  color: #455A64;
  cursor: pointer;
  transition: all 0.2s;
}

.tag-chip:hover {
  background: rgba(69, 90, 100, 0.1);
  color: #37474F;
}

.tag-chip.selected {
  background: #455A64;
  border-color: #455A64;
  color: #ECEFF1;
}

.tag-required {
  color: #d32f2f;
  font-size: max(9px, calc(1.2 * var(--u)));
  margin-left: max(4px, calc(0.5 * var(--u)));
}

.terms-section {
  margin-top: max(8px, calc(1 * var(--u)));
  padding: max(8px, calc(1 * var(--u)));
  background: rgba(0, 0, 0, 0.06);
  border-radius: 4px;
  border-left: 3px solid rgba(0, 0, 0, 0.25);
}

.terms-text {
  font-size: max(10px, calc(1.3 * var(--u)));
  color: rgba(0, 0, 0, 0.7);
  margin-bottom: max(4px, calc(0.5 * var(--u)));
}

.terms-checkbox {
  margin-bottom: 0;
}

/* 同意項目ではなく告知。作者に選択肢が無い（作品ごとの opt-out は存在せず、
   止める手段は非公開に戻すことだけ）ので、チェックボックスにはしない。 */
.terms-note {
  margin-top: max(6px, calc(0.8 * var(--u)));
  font-size: max(9px, calc(1.1 * var(--u)));
  color: rgba(0, 0, 0, 0.7);
  line-height: 1.3;
}

.terms-checkbox :deep(.v-label) {
  font-size: max(9px, calc(1.1 * var(--u)));
  font-weight: bold;
  line-height: 1.3;
}

/* オプション宣言（任意）— terms とは性質が異なるため別セクションとして表示 */
.options-section {
  margin-top: max(8px, calc(1 * var(--u)));
  padding: max(8px, calc(1 * var(--u)));
  background: rgba(0, 100, 180, 0.05);
  border-radius: 4px;
  border-left: 3px solid rgba(0, 100, 180, 0.4);
}

.options-text {
  font-size: max(10px, calc(1.3 * var(--u)));
  color: rgba(0, 0, 0, 0.7);
  margin-bottom: max(4px, calc(0.5 * var(--u)));
}

.license-select {
  max-width: 460px;
}

.options-note {
  font-size: 0.78rem;
  opacity: 0.8;
  margin-top: 6px;
}

.options-link {
  color: inherit;
  text-decoration: underline;
}

.options-checkbox {
  margin-bottom: 0;
}

.options-checkbox :deep(.v-label) {
  font-size: max(9px, calc(1.1 * var(--u)));
  line-height: 1.3;
}

.check-section {
  margin-top: max(8px, calc(1 * var(--u)));
  padding-top: max(8px, calc(1 * var(--u)));
  border-top: 1px solid rgba(0, 0, 0, 0.1);
}

.check-row {
  display: grid;
  grid-template-columns: auto 1fr;
  gap: max(8px, calc(1 * var(--u)));
}

.check-btn {
  min-width: max(140px, calc(18 * var(--u)));
  height: 100%;
  font-size: max(11px, calc(1.5 * var(--u)));
  border-radius: 4px;
}

.slug-field {
  min-width: 0;
}

.screening-status {
  margin-top: max(6px, calc(0.8 * var(--u)));
  padding: max(6px, calc(0.8 * var(--u))) max(8px, calc(1 * var(--u)));
  border-radius: 4px;
  font-size: max(10px, calc(1.3 * var(--u)));
  line-height: 1.5;
  min-height: max(28px, calc(3.5 * var(--u)));
  transition: background 0.3s, border-color 0.3s, color 0.3s;
}

.screening-status--idle {
  background: transparent;
  border: 1px solid transparent;
  color: transparent;
}

.screening-status--passed {
  background: rgba(46, 125, 50, 0.08);
  border: 1px solid rgba(46, 125, 50, 0.3);
  color: #2e7d32;
}

.screening-status--failed {
  background: rgba(211, 47, 47, 0.08);
  border: 1px solid rgba(211, 47, 47, 0.3);
  color: #c62828;
}

.screening-rules-link {
  display: inline-block;
  margin-left: max(6px, calc(0.8 * var(--u)));
  color: #c62828;
  text-decoration: underline;
  font-weight: 600;
  white-space: nowrap;
}

.screening-rules-link:hover {
  text-decoration: none;
}
</style>
